import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import uuid from "react-uuid";
import { Accordion, AccordionDetails, AccordionSummary, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel, InputBase, TextField, ToggleButton, ToggleButtonGroup, Typography, useTheme } from "@mui/material";
import { ExpandMore, Refresh, Search } from "@mui/icons-material";
import { deleteEconomics, selectEconomics } from "../reducers/economicsSlice";
import { getTypeCurves, selectTypeCurves } from "../reducers/typeCurveSlice";
import { selectWells } from "../reducers/wellSlice";
import { defaultEconomicsSettings } from "../helpers/defaultEconomicsSettings";
import { EconomicsTypeCurvesTransferList } from "./EconomicsTypeCurvesTransferList";
import { EconomicsWellsTransferList } from "./EconomicsWellsTransferList";
import { selectUser } from "../reducers/userSlice";

import { addProject, selectProjects, updateProject } from "../reducers/projectSlice";
import { selectLists } from "../reducers/listSlice";
import { EconomicsListsTransferList } from "./EconomicsListsTransferList";
import { getGroupWells, selectGroupWells } from "../reducers/groupWellSlice";
import { EconomicsGroupWellsTransferList } from "./EconomicsGroupWellsTransferList";

import { useSnackbar } from "notistack";
import { formatUwi } from "../helpers/formatUwi";
import { useSocket } from "../context/useSocket";
import { EconomicsEconomicsTransferList } from "./EconomicsEconomicsTransferList";
import { makeStyles } from "@mui/styles";
import { setIsLoadingData } from "../reducers/optionsSlice";

const useStyles = makeStyles((theme) => ({
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    marginBottom: 5,
    paddingLeft: 5,
    width: 300,
    // border: `1px solid ${theme.palette.primary.main}`,
    height: 36,
  },
  searchIcon: {
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // color: theme.palette.primary.main
  },
  inputRoot: {
    color: 'inherit'
  },
  inputInput: {
    // vertical padding + font size from searchIcon
    paddingLeft: 24
  }
}));

export const EditEconomics = ({ handleEditEconomicsClose, economics }) => {
  
  const { socket } = useSocket();

  const classes = useStyles();
  const theme = useTheme();

  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  const projects = useSelector(selectProjects);
  const currentProject = projects.byId[projects.currentProjectId];
  const lists = useSelector(selectLists);
  const wells = useSelector(selectWells);
  const typeCurves = useSelector(selectTypeCurves);
  const groupWells = useSelector(selectGroupWells);
  const allEconomics = useSelector(selectEconomics);

  const priceDeckName = economics?.settings?.priceDeckName || (allEconomics?.prices && Object.keys(allEconomics.prices)?.[0]);

  let economicsSettings = (economics?.settings || defaultEconomicsSettings);
  if (
    !economicsSettings?.priceOil?.length > 0 || 
    !economicsSettings?.priceGas?.length > 0 || 
    !economicsSettings?.priceCondensate?.length > 0 ||
    !economicsSettings?.pricePropane?.length > 0 ||
    !economicsSettings?.priceButane?.length > 0 ||
    !economicsSettings?.pricePentanes?.length > 0
  ) {
    economicsSettings = handleSetPrices(allEconomics.prices, economicsSettings, priceDeckName);
  };

  if (!economicsSettings?.priceDeckName?.length > 0 && priceDeckName) {
    economicsSettings.priceDeckName = priceDeckName;
  };

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();


  const [economicsName, setEconomicsName] = useState(economics?.name || "New economics forecast");
  const [economicsDescription, setEconomicsDescription] = useState(economics?.description || "");
  const [consolidation, setConsolidation] = useState(economics?.settings?.consolidation?.length > 0 ? economics?.settings?.consolidation : 'full-cycle');
  const [listIds, setListIds] = useState(economics?.economicsWells?.lists?.map(list => list.id) || []);
  const [uwis, setUwis] = useState(economics?.economicsWells?.wells?.map(well => well.id) || []);
  const [typeCurveIds, setTypeCurveIds] = useState(economics?.economicsWells?.typeCurves?.map(tc => tc.id) || []);
  const [groupWellIds, setGroupWellIds] = useState(economics?.economicsWells?.groupWells?.map(gw => gw.id) || []);
  const [economicsIds, setEconomicsIds] = useState(economics?.economicsIds || []);
  const [isLoadingTypeCurves, setIsLoadingTypeCurves] = useState(false);
  const [isLoadingGroupWells, setIsLoadingGroupWells] = useState(false);
  const [isLoadingEconomics, setIsLoadingEconomics] = useState(false);
  const [searchListName, setSearchListName] = useState('');
  const [searchUWI, setSearchUWI] = useState('');
  const [searchTypeCurveName, setSearchTypeCurveName] = useState('');
  const [searchGroupWellName, setSearchGroupWellName] = useState('');
  const [searchEconomicsName, setSearchEconomicsName] = useState('');

  const hasUnprocessedWells = useMemo(() => {
    const unprocessedWells = [];
    economics?.economicsWells?.wells?.forEach(well => {
      if (wells?.byUwi?.[well.id]?.status !== 'processed') {
        unprocessedWells.push(well.id);
      };
    });

    return unprocessedWells?.length > 0;
  }, [economics?.economicsWells?.wells, wells?.byUwi]);

  let filteredListsIds = currentProject ? [...currentProject?.listIds] : [];
  if (searchListName?.length > 0) {
    filteredListsIds = filteredListsIds?.filter(listId => lists?.byId?.[listId]?.name?.toLowerCase()?.includes(searchListName?.toLowerCase()));
  };

  let allUwis = [...getProjectWells(currentProject, lists)];
  if (searchUWI?.length > 0) {
    allUwis = allUwis.filter(uwi => uwi?.toLowerCase()?.includes(searchUWI?.toLowerCase()));
  };

  let filteredTypeCurveIds = typeCurves ? [...typeCurves?.allIds] : [];
  if (searchTypeCurveName?.length > 0) {
    filteredTypeCurveIds = filteredTypeCurveIds.filter(tcId => typeCurves?.byId?.[tcId]?.name?.toLowerCase()?.includes(searchTypeCurveName?.toLowerCase()));
  };

  let filteredGroupWellIds = groupWells ? [...groupWells?.allIds] : [];
  if (searchGroupWellName?.length > 0) {
    filteredGroupWellIds = filteredGroupWellIds.filter(gwId => groupWells?.byId?.[gwId]?.name?.toLowerCase()?.includes(searchGroupWellName?.toLowerCase()));
  };

  let filteredEconomicsIds = currentProject ? [...currentProject?.economicsIds] : [];
  filteredEconomicsIds = filteredEconomicsIds.filter(econId => !allEconomics?.byId?.[econId]?.economicsIds?.length > 0);
  if (searchEconomicsName?.length > 0) {
    filteredEconomicsIds = filteredEconomicsIds.filter(econId => allEconomics?.byId?.[econId]?.name?.toLowerCase()?.includes(searchEconomicsName?.toLowerCase()));
  };

  return (
    <Dialog 
      open={true} 
      onClose={handleEditEconomicsClose}
      fullWidth={true}
      maxWidth="md"
    >
      <DialogTitle>{`${economics?.id ? 'Edit' : 'New'} Economics Forecast`}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="normal"
          id="name"
          label="Economics Forecast Name"
          placeholder="Economics Forecast Name"
          type="text"
          fullWidth
          value={economicsName}
          variant="outlined"
          onChange={e => setEconomicsName(e.target.value)}
        />

        <TextField
          margin="normal"
          id="description"
          label="Description"
          placeholder="Description"
          type="text"
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          value={economicsDescription}
          variant="outlined"
          onChange={e => setEconomicsDescription(e.target.value)}
        />

        <FormControl component="fieldset" style={{width: '100%', marginTop: 20, marginBottom: 20}}>
          <FormLabel component="legend">Consolidation</FormLabel>
          <ToggleButtonGroup
            value={consolidation}
            exclusive
            onChange={updateConsolidation}
            style={{width: '100%'}}
          >
            <ToggleButton value="full-cycle" style={{width: '50%'}} color="primary">
              Full-cycle
            </ToggleButton>
            <ToggleButton value="forward-looking" style={{width: '50%'}} color="primary">
              Forward-looking
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Add lists</Typography>
          </AccordionSummary>
          <AccordionDetails>
            
            <div style={{padding: '0 0 20px 0', margin: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
              <Typography variant="caption" color={'textSecondary'} style={{}}>Lists: {currentProject?.listIds?.length || 0}</Typography>

              <div className={classes.search} style={{border: `1px solid ${darkMode ? "lightgrey" : theme.palette.primary.main}`}}>
                <div className={classes.searchIcon}>
                  <Search color={darkMode ? "inherit" : "primary"} />
                </div>
                <InputBase
                  placeholder="Search lists by name"
                  classes={{
                    root: classes.inputRoot,
                    input: classes.inputInput,
                  }}
                  fullWidth
                  value={searchListName}
                  onChange={e => setSearchListName(e.target.value)}
                />
              </div>
              <EconomicsListsTransferList 
                allLists={filteredListsIds} 
                listIds={listIds} 
                setListIds={setListIds} 
              />
            </div>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Add wells</Typography>
          </AccordionSummary>
          <AccordionDetails>
            
            <div style={{padding: '0 0 20px 0', margin: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
              <Typography variant="caption" color={'textSecondary'}>Wells: {getProjectWells(currentProject, lists)?.length || 0}</Typography>

              <div className={classes.search} style={{border: `1px solid ${darkMode ? "lightgrey" : theme.palette.primary.main}`}}>
                <div className={classes.searchIcon}>
                  <Search color={darkMode ? "inherit" : "primary"} />
                </div>
                <InputBase
                  placeholder="Search wells by UWI"
                  classes={{
                    root: classes.inputRoot,
                    input: classes.inputInput,
                  }}
                  fullWidth
                  value={searchUWI}
                  onChange={e => setSearchUWI(e.target.value)}
                />
              </div>
              <EconomicsWellsTransferList 
                wells={allUwis} 
                uwis={uwis} 
                typeCurves={typeCurves} 
                typeCurveIds={typeCurveIds} 
                setUwis={setUwis} 
              />
            </div>
            
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Add type curves</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{padding: 0, margin: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%'}}>
              <Typography variant="caption" color={'textSecondary'}>Type curves: {typeCurves?.allIds?.length || 0}</Typography>
              
              <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                <div className={classes.search} style={{border: `1px solid ${darkMode ? "lightgrey" : theme.palette.primary.main}`}}>
                  <div className={classes.searchIcon}>
                    <Search color={darkMode ? "inherit" : "primary"} />
                  </div>
                  <InputBase
                    placeholder="Search type curves by name"
                    classes={{
                      root: classes.inputRoot,
                      input: classes.inputInput,
                    }}
                    fullWidth
                    value={searchTypeCurveName}
                    onChange={e => setSearchTypeCurveName(e.target.value)}
                  />
                </div>
                <Button variant="outlined" onClick={e => handleRefreshTypeCurves(e)} startIcon={<Refresh />}>Refresh</Button>
              </div>
              <EconomicsTypeCurvesTransferList 
                allTypeCurveIds={filteredTypeCurveIds}
                typeCurveIds={typeCurveIds} 
                setTypeCurveIds={setTypeCurveIds} 
                isLoadingData={isLoadingTypeCurves}
                setIsLoadingData={setIsLoadingTypeCurves}
                userEmail={user?.value?.email}
              />
            </div>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Add group wells</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{padding: 0, margin: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%'}}>
              <Typography variant="caption" color={'textSecondary'}>Group wells: {groupWells?.allIds?.length || 0}</Typography>
              
              <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                <div className={classes.search} style={{border: `1px solid ${darkMode ? "lightgrey" : theme.palette.primary.main}`}}>
                  <div className={classes.searchIcon}>
                    <Search color={darkMode ? "inherit" : "primary"} />
                  </div>
                  <InputBase
                    placeholder="Search group wells by name"
                    classes={{
                      root: classes.inputRoot,
                      input: classes.inputInput,
                    }}
                    fullWidth
                    value={searchGroupWellName}
                    onChange={e => setSearchGroupWellName(e.target.value)}
                  />
                </div>
                <Button variant="outlined" onClick={e => handleRefreshGroupWells(e)} startIcon={<Refresh />}>Refresh</Button>
              </div>
              <EconomicsGroupWellsTransferList 
                allGroupWellIds={filteredGroupWellIds}
                groupWellIds={groupWellIds} 
                setGroupWellIds={setGroupWellIds} 
                isLoadingData={isLoadingGroupWells}
                setIsLoadingData={setIsLoadingGroupWells}
                userEmail={user?.value?.email}
              />
            </div>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />} >
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
              <Typography style={{marginRight: 10}}>Roll up economics</Typography> 
              <BetaTag />
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{padding: 0, margin: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%'}}>
              <Typography variant="caption" color={'textSecondary'}>Economics: {currentProject?.economicsIds?.length || 0}</Typography>
              
              <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                <div className={classes.search} style={{border: `1px solid ${darkMode ? "lightgrey" : theme.palette.primary.main}`}}>
                  <div className={classes.searchIcon}>
                    <Search color={darkMode ? "inherit" : "primary"} />
                  </div>
                  <InputBase
                    placeholder="Search economics by name"
                    classes={{
                      root: classes.inputRoot,
                      input: classes.inputInput,
                    }}
                    fullWidth
                    value={searchEconomicsName}
                    onChange={e => setSearchEconomicsName(e.target.value)}
                  />
                </div>
              </div>
            
              <EconomicsEconomicsTransferList
                projectEconomicsIds={filteredEconomicsIds}
                economicsIds={economicsIds} 
                setEconomicsIds={setEconomicsIds}
                isLoadingData={isLoadingEconomics}
                setIsLoadingData={setIsLoadingEconomics} 
              />
            </div>
          </AccordionDetails>
        </Accordion>

      </DialogContent>
      <DialogActions style={{display: 'flex', justifyContent: 'space-between'}}>
        <Button onClick={(e) => handleDeleteEconomics(economics.id)} color="secondary">
          Delete
        </Button>
        <div>
          <Button onClick={handleEditEconomicsClose} color="primary" style={{marginRight: 10}}>
            Cancel
          </Button>
          <Button onClick={handleUpdateEconomics} color="primary" variant="contained" disabled={hasUnprocessedWells}>
            {economics?.id ? 'Update' : 'Generate'}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );

  // FUNCTIONS
  function updateConsolidation(e, newValue) {
    newValue && setConsolidation(newValue);
  };

  function handleRefreshTypeCurves(e) {
    e.stopPropagation();
    setIsLoadingTypeCurves(true);
    dispatch(getTypeCurves({email: user?.value?.email})).then(() => setIsLoadingTypeCurves(false));
  };

  function handleRefreshGroupWells(e) {
    e.stopPropagation();
    setIsLoadingGroupWells(true);
    dispatch(getGroupWells(user?.value?.email)).then(() => setIsLoadingGroupWells(false));
  };

  

  async function handleUpdateEconomics() {
    try {
      console.log('Calculating economics forecast');

      enqueueSnackbar(`Processing economics ${economicsName}...`, {
        variant: 'info',
        persist: true
      });

      handleEditEconomicsClose();
      dispatch(setIsLoadingData(true));

      let sourceData = null;
      let economicsWells = null;
      let economicsAnalyses = null;
      let startDate = '';

      if (!economicsIds || !economicsIds?.length > 0) {
        sourceData = await getSourceData(
          listIds,
          lists,
          uwis,
          setUwis,
          wells,
          typeCurveIds,
          typeCurves,
          groupWellIds,
          groupWells,
          economicsSettings
        ); // each item is an object with production and forecast properties

        economicsWells = compileEconomicsWells(listIds, lists, uwis, setUwis, typeCurveIds, typeCurves, groupWellIds, groupWells);


        // determine the start date
        const startYears = [];
        sourceData?.forEach(source => {
          source.fluids.forEach(fluid => {
            startYears.push(source?.byFluid?.[fluid]?.[0]?.year);
          });
        });
        const startYear = Math.min(...startYears); 
        startDate = `${startYear - 1}-01-01`; // set start date as January 1 of the previous year


      } else {
        economicsIds.forEach(economicsId => {
          if (allEconomics?.byId?.[economicsId]?.cashflowNet && allEconomics?.byId?.[economicsId]?.forecastNet) {
            if (!economicsAnalyses) {
              economicsAnalyses = [];
            };
            economicsAnalyses.push({
              cashflowNet: allEconomics?.byId?.[economicsId]?.cashflowNet, 
              cashflowByFluidNet: allEconomics?.byId?.[economicsId]?.cashflowByFluidNet, 
              cashflowGross: allEconomics?.byId?.[economicsId]?.cashflowGross, 
              cashflowByFluidGross: allEconomics?.byId?.[economicsId]?.cashflowByFluidGross, 
              forecastNet: allEconomics?.byId?.[economicsId]?.forecastNet,
              forecastCompanyNet: allEconomics?.byId?.[economicsId]?.forecastCompanyNet,
              forecastGross: allEconomics?.byId?.[economicsId]?.forecastGross,
              settings: allEconomics?.byId?.[economicsId]?.settings
            });
          };
        });
      };

      let newEconomics = {
        id: economics?.id || uuid(),
        name: economicsName,
        projectId: currentProject?.id,
        createdBy: user?.value?.email,
        createdOn: economics?.createdOn || new Date(),
        lastModified: new Date(),
        description: economicsDescription,
        settings: {...economicsSettings, consolidation},
        lastSavedSettings: {...economicsSettings, consolidation},
        economicsWells,
        economicsIds
      };

      if (!newEconomics?.settings?.startDate || newEconomics?.settings?.startDate?.length === 0) {
        newEconomics.settings.startDate = startDate;
      };

      socket.send(JSON.stringify({
        messageType: 'economics', 
        payload: {sourceData: sourceData, economics: newEconomics, economicsAnalyses: economicsAnalyses}, 
        userEmail: user?.value?.email
      }));

      // update project with new economics id
      if (!currentProject?.economicsIds?.includes(newEconomics.id)) {
        const newProject = {...currentProject};
        if (!newProject.economicsIds) newProject.economicsIds = [];
        newProject.economicsIds = [...newProject?.economicsIds, newEconomics.id];
        dispatch(updateProject({ project: newProject, projectId: newProject.id }));
      };      
      

      // worker.postMessage({ sourceData, economicsSettings: newEconomics.settings, consolidation });

      // worker.onmessage = (e) => {
      //   const { cashflow, npv, newEconomicSettings, forecast } = e?.data;
      //   newEconomics.cashflow = cashflow;
      //   newEconomics.npv = npv;
      //   newEconomics.settings = newEconomicSettings;
      //   newEconomics.forecast = forecast;
      //   dispatch(updateEconomics({ economics: newEconomics, projectId: currentProject?.id })).then(() => handleEditEconomicsClose());
      //   closeSnackbar();
      //   enqueueSnackbar(`Economics ${economicsName} processed successfully`, {variant: 'success'});
      // };

      
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleDeleteEconomics(economicsId) {
    try {
      
      dispatch(deleteEconomics(economicsId)).then(() => {
        
        // remove economics from all projects
        for (const projectId of projects.allIds) {
          const project = projects.byId[projectId];
          let economicsIds = [...project.economicsIds];
          if (economicsIds.indexOf(economicsId) !== -1) {
            economicsIds = economicsIds.filter(id => id !== economicsId);
            dispatch(addProject({ project: {...project, economicsIds}, lists: project.listIds, email: user?.value?.email }));
          };
        };
        
        handleEditEconomicsClose();
      });

    } catch (error) {
      console.log(error);
    };
  };

  function getProjectWells(project, lists) {
    let wellUwis = [];
    project?.listIds?.forEach(listId => {
      lists?.byId?.[listId]?.wellUwis?.forEach(wellUwi => {
        wellUwis.indexOf(wellUwi) === -1 && wellUwis.push(wellUwi);
      });
    });

    return wellUwis;
  };

};

function BetaTag() {
  return (
    <div style={{width: 38, height: 12, borderRadius: '4px', outline: '1px solid #0758B7', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0, margin: 0}}>
      <Typography style={{fontSize: 10, fontWeight: 'bold', color: '#0758B7'}}>BETA</Typography>
    </div>
  );
};

export async function getSourceData(listIds, lists, uwis, setUwis, wells, typeCurveIds, typeCurves, groupWellIds, groupWells, economicsSettings) {
  try {
    let sourceData = [];
    const economicsWells = compileEconomicsWells(listIds, lists, uwis, setUwis, typeCurveIds, typeCurves, groupWellIds, groupWells);

    economicsWells?.wells?.forEach(well => {
      if (wells.byUwi[well.id]?.status === 'processed') {
        let count = 1;
        while (count <= well.quantity) {
          let production;
          let forecast;
          if (wells.byUwi[well.id]?.production) {
            production = resampleProductionToAnnual(wells.byUwi[well.id]?.production, 'production');
          };
          if (wells.byUwi[well.id]?.forecast) {
            forecast = resampleProductionToAnnual(wells.byUwi[well.id]?.forecast, 'forecast');
          };
          sourceData.push({ production, forecast });
          count++;
        };
      };
    });

    economicsWells?.typeCurves?.forEach(typeCurve => {
      if (typeCurves.byId[typeCurve.id]?.production || typeCurves.byId[typeCurve.id]?.forecast) {
        let count = 1;
        while (count <= typeCurve.quantity) {
          let production;
          let forecast;
          if (typeCurves.byId[typeCurve.id]?.production) {
            production = resampleProductionToAnnual(typeCurves.byId[typeCurve.id]?.production, 'production');
          };
          if (typeCurves.byId[typeCurve.id]?.forecast) {
            forecast = resampleProductionToAnnual(typeCurves.byId[typeCurve.id]?.forecast, 'forecast');
          };
          sourceData.push({ production, forecast });
          count++;
        };
      };
    });

    economicsWells?.groupWells?.forEach(groupWell => {
      if (groupWells.byId[groupWell.id]?.production || groupWells.byId[groupWell.id]?.forecast) {
        let count = 1;
        while (count <= groupWell.quantity) {
          let production;
          let forecast;
          if (groupWells.byId[groupWell.id]?.production) {
            production = resampleProductionToAnnual(groupWells.byId[groupWell.id]?.production, 'production');
          };
          if (groupWells.byId[groupWell.id]?.forecast) {
            forecast = resampleProductionToAnnual(groupWells.byId[groupWell.id]?.forecast, 'forecast');
          };
          sourceData.push({ production, forecast });
          count++;
        };
      };
    });

    sourceData = blendSources(sourceData, economicsSettings.consolidation === "full-cycle");

    return sourceData;
  } catch (error) {
    console.log(error);
    return [];
  };
};

function resampleProductionToAnnual(timeSeries, type) {
  try {
    const newTimeSeries = { byFluid: {}, fluids: [] };

    for (let fluid of Object.keys(timeSeries.byFluid)) {

      // resample monthly production time series to annual
      // Initialize variables
      const fluidTimeSeries = timeSeries.byFluid[fluid];
      let annualTimeSeries = [];

      const firstYear = new Date(fluidTimeSeries[0].date).getFullYear();
      let currentYear = firstYear;
      let currentYearP10 = 0;
      let currentYearP50 = 0;
      let currentYearP90 = 0;

      // Iterate through each cash flow entry
      for (let i = 0; i < fluidTimeSeries.length; i++) {
        const entry = fluidTimeSeries[i];
        const entryYear = new Date(entry.date)?.getFullYear();
        
        // If the year changes, add the accumulated volume for the current year to the result
        if (entryYear !== currentYear) {
          
          const volume = type === 'production' ? currentYearP50 : {
            p10: currentYearP10,
            p50: currentYearP50,
            p90: currentYearP90
          };

          annualTimeSeries.push({
            year: currentYear,
            numMonths: entryYear - 1 === firstYear ? (12 - Number(new Date(fluidTimeSeries[0].date)?.getMonth())) : 12,
            volume,
            cumVolume: 0
          });

          // Reset variables for the new year
          currentYear = entryYear;
          currentYearP10 = 0;
          currentYearP50 = 0;
          currentYearP90 = 0;
        };

        // Accumulate cash flow and count months for the current year
        currentYearP10 += type === 'production' ? entry?.volume : entry?.volume?.p10;
        currentYearP50 += type === 'production' ? entry?.volume : entry?.volume?.p50;
        currentYearP90 += type === 'production' ? entry?.volume : entry?.volume?.p90;
      };

      // Add the last year's cash flow after the loop ends
      const lastVolume = type === 'production' ? currentYearP50 : {
        p10: currentYearP10,
        p50: currentYearP50,
        p90: currentYearP90
      };

      annualTimeSeries.push({
        year: currentYear,
        numMonths: Number(new Date(fluidTimeSeries?.[fluidTimeSeries?.length - 1]?.date)?.getMonth()) + 1,
        volume: lastVolume,
        cumVolume: 0
      });

      // Calculate cumulative volume
      annualTimeSeries.forEach((d,i) => {
        let cumVolume;
        if (i === 0) {
          cumVolume = type === 'production' ? d?.volume : {
            p10: d?.volume?.p10,
            p50: d?.volume?.p50,
            p90: d?.volume?.p90
          };
        } else {
          cumVolume = type === 'production' ? d?.volume + annualTimeSeries[i-1]?.cumVolume : {
            p10: d?.volume?.p10 + annualTimeSeries[i-1]?.cumVolume?.p10,
            p50: d?.volume?.p50 + annualTimeSeries[i-1]?.cumVolume?.p50,
            p90: d?.volume?.p90 + annualTimeSeries[i-1]?.cumVolume?.p90
          };
        };

        d.cumVolume = cumVolume;
      });

      newTimeSeries.byFluid[fluid] = annualTimeSeries;
      if (newTimeSeries.fluids.indexOf(fluid) === -1) newTimeSeries.fluids.push(fluid);
    };
    
    return newTimeSeries;
  } catch (error) {
    console.log(error);
    return [];
  };
};

function blendSources(sourceData, fullCycle) {
  try {
  
    const blendedSources = [];
    const productionLengths = [];
    let fluids = [];

    for (let source of sourceData) { // construct blended time series for each source

      let sourceProductionLength = 0;

      if (source.production && source.production.fluids?.length > 0) { // needs to have production data
        
        let blendedSource = {...source?.production};
        
        for (let fluid of blendedSource?.fluids) {
          
          if (blendedSource?.length > sourceProductionLength) {
            sourceProductionLength = blendedSource?.length;
          };

          if (fluids.indexOf(fluid) === -1) {
            fluids.push(fluid);
          };
  
          if (blendedSource?.byFluid?.[fluid]) {
            const lastYear = blendedSource?.byFluid?.[fluid]?.slice(-1)?.[0]?.year;
            let wellTimeseries = [];
            blendedSource?.byFluid?.[fluid]?.forEach(p => {
              if (p.year < lastYear) {
                wellTimeseries.push({
                  year: Number(p.year),
                  numMonths: p.numMonths,
                  volume: {
                    p10: (p.volume || 0), 
                    p50: (p.volume || 0), 
                    p90: (p.volume || 0)
                  },
                  cumVolume: 0,
                  numSources: 1
                });
              };
            });
            
            if (source?.forecast?.byFluid?.[fluid]?.length > 0) {
              source?.forecast?.byFluid?.[fluid]?.forEach(f => {
                if (f.year >= lastYear) {
                  wellTimeseries.push({
                    year: Number(f.year),
                    numMonths: f.numMonths,
                    volume: {
                      p10: (f.volume?.p10 || 0), 
                      p50: (f.volume?.p50 || 0), 
                      p90: (f.volume?.p90 || 0)
                    },
                    cumVolume: 0,
                    numSources: 1
                  });
                };
              });
            };

            if (!fullCycle) { // if forward-looking only, filter out historic production
              wellTimeseries = wellTimeseries.filter(p => p.year >= Number(new Date()?.getFullYear()));
            };
  
            blendedSource.byFluid = {...blendedSource.byFluid, [fluid]: wellTimeseries};
          };

        };
        blendedSources.push(blendedSource);
        productionLengths.push(sourceProductionLength);
      };
    };

    return blendedSources;
  } catch (error) {
    console.log(error);
  };
};

export function compileEconomicsWells(listIds, lists, uwis, setUwis, typeCurveIds, typeCurves, groupWellIds, groupWells) { // compile a list of well lists, wells, type curves and group wells as inputs to the economics forecast
  let newEconomicsWells = {
    lists: [],
    wells: [],
    typeCurves: [],
    groupWells: [],
  };

  if (listIds?.length > 0) {
    listIds?.forEach(listId => { // add lists
    newEconomicsWells.lists.push({
      id: listId,
      name: lists.byId[listId].name || listId,
      quantity: 1
    });

    let wellUwis = lists.byId[listId]?.wellUwis;
    for (let uwi of wellUwis) {
      if (uwis?.length === 0) {
        setUwis([uwi]);
      } else if (!uwis?.includes(uwi)) {
        setUwis([...uwis, uwi]);
      };
      if (!newEconomicsWells?.wells?.find(w => w.id === uwi)) {
        newEconomicsWells.wells.push({
          id: uwi,
          name: formatUwi(uwi),
          quantity: 1
        });
      };
    };

    })
  } else if (uwis?.length > 0) {
    uwis?.forEach(uwi => { // add wells
     newEconomicsWells.wells.push({
       id: uwi,
       name: formatUwi(uwi),
       quantity: 1
     });
   });
  } else if (typeCurveIds?.length > 0) {
    typeCurveIds?.forEach(typeCurveId => { // add type curves
      newEconomicsWells.typeCurves.push({
        id: typeCurveId,
        name: typeCurves?.byId[typeCurveId]?.name || typeCurveId,
        quantity: 1
      });
    });
  } else if (groupWellIds?.length > 0) {
    groupWellIds?.forEach(groupWellId => { // add group wells
      newEconomicsWells.groupWells.push({
        id: groupWellId,
        name: groupWells?.byId[groupWellId]?.name || groupWellId,
        quantity: 1
      });
    });
  };

  return newEconomicsWells;
};

export function handleSetPrices(prices, settings, priceDeckName) {
  let newSettings = {...settings};
  ['priceOil', 'priceGas', 'priceCondensate', 'pricePropane', 'priceButane', 'pricePentanes'].forEach(param => {
    if (!newSettings[param]?.length > 0) {
      const commodity = getDefaultCommodity(param, priceDeckName);
      newSettings[param] = prices?.[priceDeckName]?.map(p => {
        return {
          date: p.date,
          price: p[commodity],
          commodity,
          cadToUsd: p.cadToUsd || prices?.[priceDeckName]?.slice(-1)?.[0]?.cadToUsd || 0.75
        };
      });
    };
  });

  function getDefaultCommodity(param, priceDeckName) {
    switch(priceDeckName) {
      case '3 Consultants Average Escalating':
        if (param === 'priceOil') {
          return 'Western Canadian Select (CAD/bbl)';
        } else if (param === 'priceGas') {
          return 'Alberta AECO (CAD/MMBtu)';
        } else if (param === 'priceCondensate') {
          return 'Edmonton Cond. And Nat. Gasolines (CAD/bbl)';
        } else if (param === 'pricePropane') {
          return 'Edmonton Propane (CAD/bbl)';
        } else if (param === 'priceButane') {
          return 'Edmonton Butane (CAD/bbl)';
        } else if (param === 'pricePentanes') {
          return 'Edmonton Cond. And Nat. Gasolines (CAD/bbl)';
        } else if (param === 'price') {
          return '';
        };
        break;
      default:
        return '';
    };
  };

  return newSettings;
};